import { createTheme } from "@mui/material/styles";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import React from "react";
import theme, { pinwheelPalette } from "../styles/theme";

// extend the TypographyOptions interface to add our custom variants
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    display1?: true;
    body3?: true;
    body4?: true;
    body5?: true;
    body6?: true;
  }
}

declare module "@mui/material/styles/createPalette" {
  interface TypeText {
    helper?: string;
  }
}

// create an interface for our custom variants
interface ExtendedTypographyOptions extends TypographyOptions {
  display1?: React.CSSProperties;
  body3?: React.CSSProperties;
  body4?: React.CSSProperties;
  body5?: React.CSSProperties;
  body6?: React.CSSProperties;
}

export const pinwheelTypography = {
  display1: {
    fontSize: "32px",
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  h1: {
    fontSize: "26px",
    fontWeight: 500,
    color: theme.palette.text.primary,
    lineHeight: "normal",
  },
  h2: {
    fontSize: "22px",
    fontWeight: 500,
    color: theme.palette.text.primary,
    lineHeight: "normal",
  },
  h3: {
    fontSize: "18px",
    fontWeight: 500,
    color: theme.palette.text.primary,
    lineHeight: "normal",
  },
  body1: {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "normal",
    color: theme.palette.text.primary,
  },
  body2: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "normal",
    color: theme.palette.text.primary,
  },
  body3: {
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "normal",
    color: theme.palette.text.primary,
    fontFamily: "Poppins",
  },
  body4: {
    fontSize: "16px",
    fontWeight: 400,
    color: pinwheelPalette.black[60],
    fontFamily: "Poppins",
    lineHeight: "normal",
  },
  body5: {
    fontSize: "14px",
    fontWeight: 400,
    color: pinwheelPalette.black[60],
    fontFamily: "Poppins",
    lineHeight: "normal",
  },
  body6: {
    fontSize: "12px",
    fontWeight: 400,
    color: pinwheelPalette.black[60],
    fontFamily: "Poppins",
    lineHeight: "normal",
  },
  caption: {
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "normal",
    fontFamily: "Poppins",
  },
  button: {
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "Poppins",
    lineHeight: "normal",
  },
};

export const typographyTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 769,
      lg: 1024,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      main: pinwheelPalette.purple[100],
    },
    secondary: {
      main: pinwheelPalette.yellow[100],
    },
    error: {
      main: pinwheelPalette.red[100],
    },
    info: {
      main: pinwheelPalette.blue[100],
    },
    success: {
      main: pinwheelPalette.green[100],
    },
    warning: {
      main: pinwheelPalette.orange[100],
    },
    text: {
      primary: pinwheelPalette.black[87],
      helper: pinwheelPalette.black[60],
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: "Poppins",
    button: {
      textTransform: "capitalize",
      ...pinwheelTypography.button,
    },
    display1: {
      ...pinwheelTypography.display1,
    },
    h1: {
      ...pinwheelTypography.h1,
    },
    h2: {
      ...pinwheelTypography.h2,
    },
    h3: {
      ...pinwheelTypography.h3,
    },
    body1: {
      ...pinwheelTypography.body1,
    },
    body2: {
      ...pinwheelTypography.body2,
    },
    body3: {
      ...pinwheelTypography.body3,
    },
    body4: {
      ...pinwheelTypography.body4,
    },
    body5: {
      ...pinwheelTypography.body5,
    },
    body6: {
      ...pinwheelTypography.body6,
    },
    caption: {
      ...pinwheelTypography.caption,
    },
  } as ExtendedTypographyOptions,
  components: {
    MuiButton: {
      defaultProps: {
        variant: "contained",
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          // boxShadow and border have a hardcoded value temporarily until design is finalized
          boxShadow: "none",
          border: "1px solid #e6e6e6",
          padding: "1rem",
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          // this is the backdrop that appears behind the modal
          // the "B3" is the 70% opacity added to the end of the color hex
          backgroundColor: pinwheelPalette.grey[50] + "B3",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          ...pinwheelTypography.caption,
          transform: "translate(14px, -9px) scale(1)",
        },
        root: {
          color: pinwheelPalette.black[60],
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          ...pinwheelTypography.body1,
          color: pinwheelPalette.black[87],
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          ...pinwheelTypography.body1,
        },
        input: {
          color: `${pinwheelPalette.black[87]}`,
          padding: "15.5px 14px !important",
          "&::placeholder": {
            ...pinwheelTypography.body4,
            color: pinwheelPalette.black[60],
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            padding: "0",
          },
        },
        tag: {
          ...pinwheelTypography.caption,
          color: pinwheelPalette.black[100],
          "&.Mui-disabled": {
            color: pinwheelPalette.lightGrey[50],
          },
        },
        input: {
          "&::placeholder": {
            ...pinwheelTypography.body4,
          },
        }
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...pinwheelTypography.body1,
          color: theme.palette.text.primary,
          "&.active": {
            ["& .MuiListItemIcon-root, & .MuiTypography-root"]: {
              color: theme.palette.primary.main,
            },
          },
          ["& .MuiTypography-body1"]: {
            color: theme.palette.text.primary,
          },
        },
      },
    },
  },
});
